import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  Paper,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";

const TransactionDetailsDialog = ({ transaction, open, onClose }) => {
  const { t } = useTranslation();

  if (!transaction) return null;

  const formatBytes = (bytes) => {
    if (bytes === 0) return t("common.units.bytes", { count: 0 });
    const k = 1024;
    const sizes = [t("common.units.bytes"), "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const esimOrder = transaction.ESIMOrder;
  const esimData =
    transaction.metadata?.esimProfile || transaction.metadata?.esimData;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t("transactionDetails.title")}
        <Typography variant="subtitle2" color="textSecondary">
          {transaction.id}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("transactionDetails.transactionInfo")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t("transactionDetails.type")}
                  </Typography>
                  <Typography>
                    {t(`transactions.types.${transaction.type}`, {
                      defaultValue: transaction.type
                        .replace("_", " ")
                        .toUpperCase(),
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t("transactionDetails.amount")}
                  </Typography>
                  <Typography>
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                      signDisplay: "always",
                    }).format(transaction.amount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t("transactionDetails.status")}
                  </Typography>
                  <Chip
                    label={t(`status.${transaction.status}`, {
                      defaultValue: transaction.status.toUpperCase(),
                    })}
                    color={
                      transaction.status === "completed"
                        ? "success"
                        : transaction.status === "pending"
                        ? "warning"
                        : "error"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">
                    {t("transactionDetails.date")}
                  </Typography>
                  <Typography>
                    {new Date(transaction.created_at).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {transaction.type === "esim_purchase" && (esimOrder || esimData) && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t("transactionDetails.esimDetails")}
                </Typography>

                {(esimOrder?.qrCodeUrl || esimData?.qrCodeUrl) && (
                  <Box sx={{ textAlign: "center", mb: 3 }}>
                    <QRCodeSVG
                      value={esimOrder?.activationCode || esimData?.ac || ""}
                      size={200}
                    />
                    <Typography variant="caption" display="block" mt={1}>
                      {t("transactionDetails.qrCodeInstruction")}
                    </Typography>
                  </Box>
                )}

                <Grid container spacing={2}>
                  {(esimOrder?.activationCode || esimData?.ac) && (
                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, bgcolor: "background.default" }}
                      >
                        <Typography variant="subtitle2" gutterBottom>
                          {t("transactionDetails.activationCode")}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            wordBreak: "break-all",
                            bgcolor: "background.paper",
                            p: 1,
                            borderRadius: 1,
                          }}
                        >
                          {esimOrder?.activationCode || esimData?.ac}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">
                      {t("transactionDetails.iccid")}
                    </Typography>
                    <Typography>
                      {esimOrder?.iccid ||
                        esimData?.iccid ||
                        t("common.noData")}
                    </Typography>
                  </Grid>

                  {esimOrder?.totalVolume && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">
                        {t("transactionDetails.dataPackage")}
                      </Typography>
                      <Typography>
                        {formatBytes(esimOrder.totalVolume)}
                      </Typography>
                    </Grid>
                  )}

                  {esimOrder?.duration && esimOrder?.durationUnit && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">
                        {t("transactionDetails.validity")}
                      </Typography>
                      <Typography>
                        {esimOrder.duration}{" "}
                        {esimOrder.durationUnit.toLowerCase()}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          )}

          {transaction.metadata?.packageName && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t("transactionDetails.packageDetails")}
                </Typography>
                <Typography>{transaction.metadata.packageName}</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t("common.actions.close")}</Button>
        {transaction.status === "completed" &&
          (esimOrder?.qrCodeUrl || esimData?.qrCodeUrl) && (
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                window.open(
                  esimOrder?.qrCodeUrl || esimData?.qrCodeUrl,
                  "_blank"
                )
              }
            >
              {t("transactionDetails.downloadQrCode")}
            </Button>
          )}
      </DialogActions>
    </Dialog>
  );
};

export default TransactionDetailsDialog;
